/**
 * One page as the parser saw it, with every recognized region drawn over it.
 *
 * Bboxes come back in pixels of the *corrected* frame — after deskew, after any dewarp —
 * so the frame is derived from the regions themselves rather than assumed, and each box
 * is placed as a percentage of it. The overlay then stays honest at any width without
 * anyone knowing the render DPI.
 *
 * A text-layer page has exact text and no pixel geometry. There is nothing to overlay
 * and inventing boxes would be a lie, so it shows its text instead.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { imageObjectUrl } from "../lib/api";
import type { Page } from "../lib/types";
import { MediaViewer } from "./MediaViewer";
import { Chip, METHOD_COLOR, METHOD_TONE } from "./ui";

/**
 * The rendered page, fetched as an object URL because `<img src>` cannot carry a bearer
 * header. A document with no printed page answers 409, which is an answer, not a
 * failure: the boxes then sit on a blank sheet, which is what the parser saw.
 */
function usePageImage(documentId: string, index: number, wanted: boolean): string | null {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!wanted) {
      setUrl(null);
      return;
    }
    let live = true;
    let created: string | null = null;
    setUrl(null);

    imageObjectUrl(`/v1/documents/${documentId}/pages/${index}/image`)
      .then((objectUrl) => {
        if (!live) {
          URL.revokeObjectURL(objectUrl);
          return;
        }
        created = objectUrl;
        setUrl(objectUrl);
      })
      .catch(() => {
        /* 409 or 404: there is no page to draw, and the overlay still stands alone */
      });

    return () => {
      live = false;
      if (created) URL.revokeObjectURL(created);
    };
  }, [documentId, index, wanted]);

  return url;
}

function band(confidence: number | undefined): { color: string; label: string } {
  if (confidence === undefined) return { color: "var(--text-faint)", label: "—" };
  if (confidence >= 0.9) return { color: "var(--gold)", label: confidence.toFixed(2) };
  if (confidence >= 0.75) return { color: "var(--warn)", label: confidence.toFixed(2) };
  return { color: "var(--crit)", label: confidence.toFixed(2) };
}

/**
 * The frame the boxes are measured in.
 *
 * The page reports it, and that is the only correct answer: the extent of the ink is
 * not the extent of the page, so deriving it from the boxes drops the margins and puts
 * every box off by the size of them. The derived value survives only as a fallback for
 * pages read before the server recorded the real one, and it is flagged as approximate
 * rather than passed off as the truth.
 */
function frame(page: Page): { width: number; height: number; exact: boolean } {
  if (page.width && page.height) {
    return { width: page.width, height: page.height, exact: true };
  }
  let width = 1;
  let height = 1;
  for (const region of page.regions ?? []) {
    width = Math.max(width, region.bbox[2]);
    height = Math.max(height, region.bbox[3]);
  }
  return { width, height, exact: false };
}

export function PageView({
  page,
  documentId,
  selected,
  onSelect,
  needle = null,
}: {
  page: Page;
  documentId: string;
  /** Index into this page's regions, or null. Owned above so the text pane can follow. */
  selected: number | null;
  onSelect: (index: number | null) => void;
  /** A term to highlight in a text-layer page's text (from a search landing). */
  needle?: string | null;
}) {
  const [showPage, setShowPage] = useState(true);
  const [showRegions, setShowRegions] = useState(true);
  const [showConfidence, setShowConfidence] = useState(true);

  const regions = page.regions ?? [];
  const box = useMemo(() => frame(page), [page]);
  const image = usePageImage(documentId, page.index, regions.length > 0);
  const doubtful = regions.filter((region) => (region.confidence ?? 1) < 0.75).length;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex shrink-0 flex-wrap items-center gap-2.5 border-b border-hair bg-hair-soft px-4 py-2">
        <h3 className="font-mono text-[11px] font-bold tracking-[0.2em] uppercase">
          page {page.index + 1}
        </h3>
        <Chip tone={METHOD_TONE[page.method] ?? "mut"}>{page.method.replace("_", " ")}</Chip>
        {page.scripts && page.scripts.length > 0 && <Chip>{page.scripts.join(" · ")}</Chip>}
        {!box.exact && regions.length > 0 && (
          <Chip tone="warn">boxes approximate — re-parse to place them exactly</Chip>
        )}
        {doubtful > 0 && (
          <Chip tone="warn">
            {doubtful} doubtful read{doubtful === 1 ? "" : "s"}
          </Chip>
        )}
        {regions.length > 0 && (
          <div className="ms-auto flex border border-hair">
            <button
              aria-pressed={showPage}
              onClick={() => setShowPage((on) => !on)}
              disabled={!image}
              title={image ? "Show the rendered page behind the regions" : "This document has no printed page"}
              className={`px-2.5 py-1 font-mono text-[10px] tracking-[0.14em] uppercase transition-colors duration-200 ease-alt disabled:opacity-40 ${
                showPage && image ? "bg-gold-surface font-bold text-on-gold" : "text-dim hover:text-gold"
              }`}
            >
              page
            </button>
            <button
              aria-pressed={showRegions}
              onClick={() => setShowRegions((on) => !on)}
              className={`px-2.5 py-1 font-mono text-[10px] tracking-[0.14em] uppercase transition-colors duration-200 ease-alt ${
                showRegions ? "bg-gold-surface font-bold text-on-gold" : "text-dim hover:text-gold"
              }`}
            >
              regions
            </button>
            <button
              aria-pressed={showConfidence}
              onClick={() => setShowConfidence((on) => !on)}
              className={`border-s border-hair px-2.5 py-1 font-mono text-[10px] tracking-[0.14em] uppercase transition-colors duration-200 ease-alt ${
                showConfidence ? "bg-gold-surface font-bold text-on-gold" : "text-dim hover:text-gold"
              }`}
            >
              conf
            </button>
          </div>
        )}
      </div>

      {regions.length === 0 ? (
        <pre className="min-h-0 flex-1 overflow-auto bg-ground-soft px-4 py-3.5 font-mono text-[11.5px] leading-relaxed whitespace-pre text-dim">
          {page.markdown ? <Marked text={page.markdown} needle={needle} /> : "This page produced no text."}
        </pre>
      ) : (
        <MediaViewer width={box.width} height={box.height}>
          {image && showPage && (
            <img
              src={image}
              alt={`Page ${page.index + 1}`}
              className="absolute inset-0 h-full w-full object-fill"
              draggable={false}
            />
          )}
          {regions.map((region, index) => {
            const [left, top, right, bottom] = region.bbox;
            const { color, label } = band(region.confidence);
            const isSelected = selected === index;
            return (
              <button
                key={index}
                onClick={() => onSelect(isSelected ? null : index)}
                title={region.text}
                className="absolute text-start"
                style={{
                  left: `${(left / box.width) * 100}%`,
                  top: `${(top / box.height) * 100}%`,
                  width: `${((right - left) / box.width) * 100}%`,
                  height: `${((bottom - top) / box.height) * 100}%`,
                  border: showRegions
                    ? `${isSelected ? 2 : 1}px solid ${color}`
                    : "1px solid transparent",
                  background: showRegions
                    ? isSelected
                      ? `color-mix(in srgb, ${color} 34%, transparent)`
                      : `color-mix(in srgb, ${color} 12%, transparent)`
                    : "none",
                  boxShadow: isSelected
                    ? `0 0 0 3px color-mix(in srgb, ${color} 34%, transparent)`
                    : "none",
                }}
              >
                {showRegions && showConfidence && (
                  <span
                    className="tnum absolute -top-[13px] start-0 px-1 font-mono text-[8px] leading-[1.6] font-bold"
                    style={{ background: color, color: "var(--ground)" }}
                  >
                    {label}
                  </span>
                )}
              </button>
            );
          })}
        </MediaViewer>
      )}

      {selected !== null && regions[selected] && (
        <div className="shrink-0 border-t border-hair bg-hair-soft px-4 py-2.5">
          <p className="eyebrow">// selected region</p>
          <p className="mt-1.5 font-mono text-[12.5px] break-words">{regions[selected].text}</p>
          <p className="tnum mt-1.5 font-mono text-[10.5px] text-faint">
            conf {band(regions[selected].confidence).label} · bbox [
            {regions[selected].bbox.map((value) => Math.round(value)).join(", ")}]
          </p>
        </div>
      )}
    </div>
  );
}

/** The page strip: one cell per page, coloured by the tier that read it. */
export function PageMap({
  pages,
  current,
  onPick,
}: {
  pages: Page[];
  current: number;
  onPick: (index: number) => void;
}) {
  return (
    <div className="flex flex-wrap gap-[3px]" aria-label="Pages by read tier">
      {pages.map((page, index) => (
        <button
          key={page.index}
          onClick={() => onPick(index)}
          title={`Page ${page.index + 1} — ${page.method}`}
          className="h-3.5 w-2 transition-opacity duration-150"
          style={{
            background: METHOD_COLOR[page.method] ?? "var(--text-faint)",
            opacity: index === current ? 1 : 0.4,
            outline: index === current ? "1px solid var(--text)" : "none",
            outlineOffset: 1,
          }}
        />
      ))}
    </div>
  );
}

/** Highlights the first exact occurrence of `needle` in a text-layer page's text and
 * scrolls it into view — the search-landing mark for pages that have no region overlay. */
function Marked({ text, needle }: { text: string; needle: string | null }) {
  const hit = useRef<HTMLElement>(null);
  const wanted = needle?.trim() ?? "";
  useEffect(() => {
    hit.current?.scrollIntoView({ block: "center", inline: "nearest" });
  }, [wanted, text]);
  if (!wanted) return <>{text}</>;
  const at = text.indexOf(wanted);
  if (at === -1) return <>{text}</>;
  return (
    <>
      {text.slice(0, at)}
      <mark ref={hit} className="bg-gold-surface text-on-gold">
        {wanted}
      </mark>
      {text.slice(at + wanted.length)}
    </>
  );
}
